import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useReaderAuth } from "@/contexts/ReaderAuthContext";

const PROFESSIONS = [
    "Specializzando in oculistica",
    "Specialista in oculistica",
    "Ortottista",
    "Amministrazione di studio medico",
    "Medico chirurgo",
    "Altro",
];

export default function ReaderRegisterPage() {
    const { register } = useReaderAuth();
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", password: "",
        phone: "", birth_date: "", city: "", profession: "",
        privacy_consent: false, marketing_consent: false,
    });
    const [busy, setBusy] = useState(false);
    const [params] = useSearchParams();
    const nav = useNavigate();
    const redirect = params.get("redirect") || "/extra";
    const fromBookId = redirect.startsWith("/extra/") ? redirect.split("/extra/")[1] : null;

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.privacy_consent || !form.marketing_consent) {
            toast.error("Per creare l'account è necessario accettare la Privacy Policy e le comunicazioni collegate ai contenuti extra");
            return;
        }
        setBusy(true);
        try {
            await register({ ...form, from_book_id: fromBookId });
            toast.success("Registrazione completata");
            nav(redirect, { replace: true });
        } catch (err) {
            toast.error(err.message || "Errore registrazione");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-lg mx-auto mt-4 md:mt-10">
            <h1 className="pvx-serif text-3xl tracking-tight mb-2">Registrati gratuitamente</h1>
            <p className="text-[13.5px] text-pvx-textmuted mb-8">
                Sblocca i contenuti extra dei libri Prime Vision. La registrazione richiede pochi secondi.
            </p>
            <form onSubmit={submit} className="pvx-card p-6 md:p-7 space-y-4" data-testid="reader-register-form">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Nome" required>
                        <input required value={form.first_name} onChange={e => set("first_name", e.target.value)}
                               data-testid="reg-first-name" className={inputCls} autoComplete="given-name" />
                    </Field>
                    <Field label="Cognome" required>
                        <input required value={form.last_name} onChange={e => set("last_name", e.target.value)}
                               data-testid="reg-last-name" className={inputCls} autoComplete="family-name" />
                    </Field>
                </div>
                <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={e => set("email", e.target.value)}
                           data-testid="reg-email" className={inputCls} autoComplete="email" />
                </Field>
                <Field label="Password" required hint="minimo 8 caratteri">
                    <input required type="password" minLength={8} value={form.password} onChange={e => set("password", e.target.value)}
                           data-testid="reg-password" className={inputCls} autoComplete="new-password" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Cellulare" required>
                        <input required value={form.phone} onChange={e => set("phone", e.target.value)}
                               data-testid="reg-phone" className={inputCls} autoComplete="tel" />
                    </Field>
                    <Field label="Data di nascita" required hint="GG/MM/AAAA">
                        <input required value={form.birth_date} onChange={e => set("birth_date", e.target.value)}
                               data-testid="reg-birth-date" className={inputCls} placeholder="GG/MM/AAAA"
                               autoComplete="bday" inputMode="numeric" />
                    </Field>
                </div>
                <Field label="Città di residenza" required>
                    <input required value={form.city} onChange={e => set("city", e.target.value)}
                           data-testid="reg-city" className={inputCls} autoComplete="address-level2" />
                </Field>
                <Field label="Professione / ruolo" required>
                    <select required value={form.profession} onChange={e => set("profession", e.target.value)}
                            data-testid="reg-profession" className={inputCls}>
                        <option value="">Seleziona la tua professione…</option>
                        {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </Field>

                <div className="pvx-divider" />

                <label className="flex items-start gap-2.5 text-[12.5px] cursor-pointer" data-testid="reg-privacy-wrap">
                    <input required type="checkbox" checked={form.privacy_consent}
                           onChange={e => { const v = e.target.checked; setForm(f => ({ ...f, privacy_consent: v, marketing_consent: v })); }}
                           data-testid="reg-privacy" className="mt-0.5 accent-pvx-primary" />
                    <span className="text-pvx-textmuted">
                        Accetto la <a href="https://www.primevision.app/privacy" target="_blank" rel="noopener noreferrer" className="text-pvx-primary font-medium hover:underline">Privacy Policy</a> e autorizzo Prime Vision a inviarmi comunicazioni e aggiornamenti collegati ai contenuti extra, anche via email e WhatsApp.
                    </span>
                </label>

                <button
                    data-testid="reg-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-pvx-primary text-white rounded-[10px] py-2.5 text-[13.5px] font-medium hover:opacity-90 disabled:opacity-60 transition-opacity"
                >
                    {busy ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <ShieldCheck size={14} strokeWidth={1.5} />}
                    Crea account
                    {!busy && <ArrowRight size={14} strokeWidth={1.5} />}
                </button>

                <div className="pt-2 text-center text-[12.5px] text-pvx-textmuted">
                    Hai già un account? <Link to={`/reader/login${redirect !== "/extra" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="text-pvx-primary font-medium hover:underline">Accedi</Link>
                </div>
            </form>
        </div>
    );
}

const inputCls = "w-full h-10 px-3 rounded-[10px] border border-pvx-border bg-pvx-surface/60 focus:bg-pvx-surface focus:border-pvx-primary/50 text-[13.5px] outline-none transition-colors";

function Field({ label, required, hint, children }) {
    return (
        <div>
            <label className="pvx-eyebrow mb-1.5 block flex items-baseline gap-1">
                <span>{label}{required && <span className="text-pvx-critical ml-0.5">*</span>}</span>
                {hint && <span className="text-pvx-textsubtle normal-case tracking-normal font-normal text-[10.5px]">· {hint}</span>}
            </label>
            {children}
        </div>
    );
}
